import { If } from 'components';
import {
  selectCurrentTeam,
  selectCurrentTeamAccountingPermissions,
  selectCurrentTeamFeePermissions,
  selectCurrentTeamJobPermissions,
  showNotifier
} from 'containers/app/appSlice';
import AddInvoiceDiscountWrapper from 'containers/invoices/create-invoice/components/AddInvoiceDiscountWrapper';
import AddItemModal, { IAddItemModalConfig } from 'containers/invoices/create-invoice/components/AddItemModal';
import AddLineItemSectionWrapper from 'containers/invoices/create-invoice/components/AddLineItemSectionWrapper';
import AddServiceChargeWrapper from 'containers/invoices/create-invoice/components/AddServiceChargeWrapper';
import { InvoiceValidationErrorType, initServiceCharge } from 'containers/invoices/create-invoice/constants';
import {
  EInvoiceCatalogElementType,
  EInvoiceModalType,
  EItemModalType,
  ISaveLineItem,
  InvoiceDraftModalData
} from 'containers/invoices/create-invoice/types';
import {
  InvoiceLineItem,
  calculateSurchargeInCurrency,
  calculateSurchargeInCurrencyWithBps
} from 'containers/invoices/InvoicesSlice';
import { DiscountFormData, LineItemAdditionalTax } from 'containers/invoices/types';
import { getAggregatedTaxInCurrency, getAggregatedTaxInCurrencyWithSymbol } from 'containers/invoices/utils';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, createContext, useEffect, useState } from 'react';
import { formattedLineItemQty, getAmountWithCurrency } from 'utils/amountUtils';
import {
  addJobLineItem,
  incrementJobLineItemQty,
  modifyJobLineItem,
  removeJobDiscountFormData,
  removeJobLineItem,
  removeJobServiceCharge,
  resetJobDraftData,
  selectAppliedJobLineItemLevelTaxes,
  selectCurrentSurchargeInJobs,
  selectGlobalLineItemTaxesJobsSlice,
  selectGlobalServiceChargeTaxesInJobSlice,
  selectIsBackFromCreateTaxFromJobsSlice,
  selectJobDiscountFormData,
  selectJobDiscountSettings,
  selectJobFinalAmount,
  selectJobInvoiceStatus,
  selectJobLineItemDraftModalData,
  selectJobLineItems,
  selectJobLineItemsTotal,
  selectJobSelectedServiceCharge,
  selectJobSubtotal,
  selectJobValidationErrors,
  selectTotalOriginalUnitCost,
  updateIsBackFromCreateTaxInJobsSlice,
  updateIsServiceFeeEnabledInJobs,
  updateJobDiscountFormData,
  updateJobDraftData,
  updateSelectedJobServiceCharge
} from '../../create-edit-job/createJobSlice';
import { LINE_ITEM_SOURCE } from 'constants/common';
import { IInvoiceServiceCharge, InvoiceStatus } from 'containers/invoices/invoices.model';
import { NotifierTypes } from 'constants/notifierConstants';
import { twMerge } from 'tailwind-merge';
import {
  useEditJobDiscountMutation,
  useEditJobLineItemMutation,
  useEditJobServiceChargeMutation
} from 'containers/jobs/api';
import { getLineItemPayload, getServiceChargePayload, prepareDiscountPayloadForCreateJob } from 'containers/jobs/utils';
import { useParams } from 'react-router-dom';
import { Job } from 'containers/jobs/jobs.model';
import { InfoMesssages } from 'types/infoMessages';
import { JobStatus } from 'containers/jobs/constant';

interface BillingTabJobLineItemSectionProps {
  className?: string;
  isLoadingTaxesAndFees: boolean;
  job: Job;
}

const addItemModalConfigInitState: IAddItemModalConfig = {
  open: false,
  data: {
    id: 0,
    name: '',
    unitPrice: '',
    sku: '',
    description: '',
    selectedTaxes: [],
    quantity: '1',
    unitCost: '',
    markupPercentage: ''
  },
  editMode: false
};

export interface ILineItemsSectionContext {
  showAddFromCatalogModal: boolean;
  setShowAddFromCatalogModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const JobLineItemsSectionContext = createContext<ILineItemsSectionContext>({
  showAddFromCatalogModal: false,
  setShowAddFromCatalogModal: undefined
});

const BillingTabJobLineItemSection: FC<BillingTabJobLineItemSectionProps> = ({ isLoadingTaxesAndFees, job }) => {
  const { rootJobId, jobId } = useParams();
  const isBackFromCreateTaxRoute = useAppSelector(selectIsBackFromCreateTaxFromJobsSlice);
  const selectedJobDraftData = useAppSelector(selectJobLineItemDraftModalData);
  const currentInvoiceValidationErrors = useAppSelector(selectJobValidationErrors);
  const currentSurchargeState = useAppSelector(selectCurrentSurchargeInJobs);
  const discountSettingsState = useAppSelector(selectJobDiscountSettings);
  const selectedServiceCharge = useAppSelector(selectJobSelectedServiceCharge);
  const lineItemsTotalState = useAppSelector(selectJobLineItemsTotal);
  const jobLineItemsState = useAppSelector(selectJobLineItems);
  const discountFormDataState = useAppSelector(selectJobDiscountFormData);
  const jobFinalAmountState = useAppSelector(selectJobFinalAmount);
  const jobSubtotalState = useAppSelector(selectJobSubtotal);
  const jobFeePermissions = useAppSelector(selectCurrentTeamFeePermissions);
  const selectedAppliedLineItemLevelTaxes = useAppSelector(selectAppliedJobLineItemLevelTaxes);
  const totalOriginalUnitCostState = useAppSelector(selectTotalOriginalUnitCost);
  const { viewItemCost } = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { modifyDoneJobs, createAndEditJob } = useAppSelector(selectCurrentTeamJobPermissions);

  const [showAddFromCatalogModal, setShowAddFromCatalogModal] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [addItemModalConfig, setAddItemModalConfig] = useState<IAddItemModalConfig>(addItemModalConfigInitState);
  const [items, setItems] = useState([]);

  const invoiceStatus = useAppSelector(selectJobInvoiceStatus);
  const isPaidInvoice =
    invoiceStatus === InvoiceStatus.PAID ||
    invoiceStatus === InvoiceStatus.PAID_QR ||
    invoiceStatus === InvoiceStatus.REFUNDED ||
    invoiceStatus === InvoiceStatus.REFUND_INITIATED;
  const isFromBillingTab = true;

  const currentTeam = useAppSelector(selectCurrentTeam);
  const [editJobDiscount] = useEditJobDiscountMutation();
  const [editJobServiceCharge] = useEditJobServiceChargeMutation();
  const [editJobLineItem, { isLoading: isEditJobLineItemLoading }] = useEditJobLineItemMutation();

  useEffect(() => {
    setItems(jobLineItemsState); // while navigating & returning, item order resets
  }, [jobLineItemsState]);

  const canEditJobs = () => {
    if (job?.status === JobStatus.DONE) {
      return modifyDoneJobs;
    }
    return createAndEditJob;
  };

  const showDraftEditItemModal = () => {
    const { modalConfig } = selectedJobDraftData ?? {};
    const itemModalConfig: IAddItemModalConfig = {
      open: true,
      data: modalConfig?.data ?? addItemModalConfigInitState,
      editMode: modalConfig?.editMode ?? false,
      draftFlow: modalConfig?.draftFlow
    };
    if (selectedJobDraftData.invoiceModalType === EInvoiceModalType.EDIT_ITEM_MODAL) {
      setAddItemModalConfig({
        data: itemModalConfig.data,
        open: true,
        editMode: true,
        draftFlow: { isDraftFlow: true, saveToCatalog: false }
      });

      // Add, one-time modals are rendered in addlineitemsection wrapper
      if (isBackFromCreateTaxRoute) {
        handleOnBackFromCreateTax();
      }
    }
  };

  const handleOnBackFromCreateTax = () => {
    dispatch(resetJobDraftData());
    dispatch(updateIsBackFromCreateTaxInJobsSlice(false));
  };

  useEffect(() => {
    if (isBackFromCreateTaxRoute) {
      if (
        selectedJobDraftData &&
        selectedJobDraftData?.invoiceCatalogElementType === EInvoiceCatalogElementType.ITEM &&
        selectedJobDraftData?.invoiceModalType === EInvoiceModalType.EDIT_ITEM_MODAL
      ) {
        showDraftEditItemModal();
      }
    }
  }, []);

  const resetItemModalConfig = () => {
    setAddItemModalConfig(addItemModalConfigInitState);
  };

  const onRemoveLineItem = async (lineItem: InvoiceLineItem) => {
    try {
      const payload = getLineItemPayload(
        lineItem,
        selectedAppliedLineItemLevelTaxes,
        jobLineItemsState,
        discountFormDataState,
        currentSurchargeState,
        selectedServiceCharge,
        'remove'
      );

      await editJobLineItem({
        teamId: currentTeam.id,
        jobId: jobId || rootJobId,
        body: payload
      })
        .unwrap()
        .then(() => {
          dispatch(removeJobLineItem(lineItem));
        });
    } catch (error) {
      // Handle error
    }
  };

  const onEditLineItem = (lineItem: InvoiceLineItem) => {
    if (isPaidInvoice) {
      dispatch(
        showNotifier({
          type: NotifierTypes.WARNING,
          message: {
            primaryMessage: 'Editing line items is disabled for jobs with paid invoice',
            isMessageHtml: false
          }
        })
      );
      return;
    }

    if (!job.isEditable) {
      dispatch(
        showNotifier({
          type: NotifierTypes.WARNING,
          message: {
            primaryMessage: InfoMesssages.cannotEditJobUnderPR,
            isMessageHtml: false
          }
        })
      );
      return;
    }

    if (!canEditJobs()) {
      dispatch(
        showNotifier({
          type: NotifierTypes.WARNING,
          message: {
            primaryMessage: InfoMesssages.editDoneJobNoPermissionMessage,
            isMessageHtml: false
          }
        })
      );
      return;
    }

    const itemData = {
      id: +lineItem.id,
      catalogueLineItemId: lineItem.catalogueLineItemId,
      name: lineItem.itemDescription,
      unitPrice: lineItem.unitCost,
      unitCost: lineItem.originalUnitCost,
      markupPercentage: lineItem.markupPercentage,
      sku: lineItem.sku,
      description: lineItem.additionalDetails,
      selectedTaxes: lineItem?.taxes ?? [],
      quantity: lineItem.quantity
    };
    setAddItemModalConfig({ open: true, data: itemData, editMode: true });
  };

  const onSaveLineItemsClick = async (lineItemData: ISaveLineItem) => {
    try {
      const payload = getLineItemPayload(
        lineItemData.invoiceLineItem,
        selectedAppliedLineItemLevelTaxes,
        jobLineItemsState,
        discountFormDataState,
        currentSurchargeState,
        selectedServiceCharge,
        'add'
      );
      await editJobLineItem({
        teamId: currentTeam.id,
        jobId: jobId || rootJobId,
        body: payload
      })
        .unwrap()
        .then(() => {
          dispatch(addJobLineItem(lineItemData.invoiceLineItem));
        });
    } catch (error) {
      // Handle error
    }

    resetItemModalConfig();
  };

  const onModifyLineItemClick = async (lineItem: InvoiceLineItem) => {
    try {
      const payload = getLineItemPayload(
        lineItem,
        selectedAppliedLineItemLevelTaxes,
        jobLineItemsState,
        discountFormDataState,
        currentSurchargeState,
        selectedServiceCharge,
        'edit'
      );

      await editJobLineItem({
        teamId: currentTeam.id,
        jobId: jobId || rootJobId,
        body: payload
      })
        .unwrap()
        .then(() => {
          dispatch(modifyJobLineItem(lineItem));
        });
    } catch (error) {
      // Handle error
    }
    resetItemModalConfig();
  };

  const isAddDiscountDisabled =
    jobLineItemsState?.length === 0 ||
    jobLineItemsState === null ||
    !jobFeePermissions.viewDiscount ||
    isPaidInvoice ||
    !job.isEditable ||
    !canEditJobs();

  const isAddServiceChargeDisabled =
    jobLineItemsState?.length === 0 || jobLineItemsState === null || isPaidInvoice || !job.isEditable || !canEditJobs();

  const getFormattedSubtotalCurrency = () => {
    if (!jobSubtotalState) {
      return getAmountWithCurrency(0);
    }
    const subTotal = jobSubtotalState;
    return getAmountWithCurrency(subTotal);
  };

  const getCurrencyFormattedFinalAmount = () => {
    let totalTax = 0;
    selectedAppliedLineItemLevelTaxes.forEach(tax => {
      totalTax += +getAggregatedTaxInCurrency(jobLineItemsState, tax, selectedServiceCharge);
    });
    const totalAmount = +jobSubtotalState + +job.surchargeAmount + totalTax + +selectedServiceCharge.totalAmount;
    return getAmountWithCurrency(totalAmount);
  };

  const getTotalUnitCost = () => {
    return getAmountWithCurrency(totalOriginalUnitCostState);
  };

  const getLineItemErrorMsg = () => {
    const lineItemError = currentInvoiceValidationErrors.find(error => {
      if (error.type === InvoiceValidationErrorType.MIN_LINE_ITEM) {
        return error;
      }
    });
    return lineItemError?.errorMsg;
  };

  const onCloseAddItemModal = (): void => {
    setAddItemModalConfig(addItemModalConfigInitState);
  };

  const getLineItemTaxesText = (lineItemTaxes: LineItemAdditionalTax[]) => {
    if (lineItemTaxes?.length === 1) return '+1 tax';
    return `+${lineItemTaxes?.length} taxes`;
  };

  const handleIncrementLineItemQty = (itemToBeAdded: InvoiceLineItem) => {
    dispatch(incrementJobLineItemQty(itemToBeAdded));
  };

  const handleUpdateInvoiceModalData = (invoiceItemModalDraft: InvoiceDraftModalData) => {
    dispatch(updateJobDraftData(invoiceItemModalDraft));
  };

  const onRemoveDiscount = (): void => {
    dispatch(removeJobDiscountFormData());
  };

  const handleSaveDiscount = async (discountFormData: DiscountFormData): Promise<void> => {
    let surchargeInCurrencyWithBps = 0;

    if (currentSurchargeState.enabled) {
      const subtotal = lineItemsTotalState - +discountFormData.discountInCurrency;

      const surchargeInCurrency: number = calculateSurchargeInCurrency(currentSurchargeState.percentage, subtotal);

      surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
        surchargeInCurrency,
        currentSurchargeState.basePoints
      );
    }

    const payload = prepareDiscountPayloadForCreateJob(discountFormData, discountSettingsState);

    try {
      await editJobDiscount({
        teamId: currentTeam.id,
        jobId: jobId || rootJobId,
        body: {
          ...payload,
          surchargeAmount: `${surchargeInCurrencyWithBps}`,
          isServiceFeeEnabled: currentSurchargeState.enabled
        }
      })
        .unwrap()
        .then(() => {
          dispatch(updateJobDiscountFormData(discountFormData));
        });
    } catch (error) {
      // error
    }
  };

  const handleIsServiceFeeEnabled = (isServiceFeeEnabled: boolean): void => {
    dispatch(updateIsServiceFeeEnabledInJobs(isServiceFeeEnabled));
  };

  const handleRemoveSelectedServiceCharge = async () => {
    try {
      const updatedServiceChargePayload = getServiceChargePayload(
        initServiceCharge,
        jobSubtotalState,
        jobLineItemsState,
        selectedServiceCharge,
        selectedAppliedLineItemLevelTaxes,
        currentSurchargeState
      );
      await editJobServiceCharge({
        teamId: currentTeam.id,
        jobId: jobId || rootJobId,
        body: updatedServiceChargePayload
      })
        .unwrap()
        .then(() => {
          dispatch(removeJobServiceCharge());
        });
    } catch (error) {
      // error
    }
  };

  const handleUpdateSelectedServiceCharge = async (serviceCharge: IInvoiceServiceCharge): Promise<void> => {
    try {
      dispatch(updateSelectedJobServiceCharge(serviceCharge));

      const updatedServiceChargePayload = getServiceChargePayload(
        serviceCharge,
        jobSubtotalState,
        jobLineItemsState,
        selectedServiceCharge,
        selectedAppliedLineItemLevelTaxes,
        currentSurchargeState
      );

      await editJobServiceCharge({
        teamId: currentTeam.id,
        jobId: jobId || rootJobId,
        body: updatedServiceChargePayload
      });
    } catch (error) {
      // error
    }
  };

  return (
    <JobLineItemsSectionContext.Provider value={{ showAddFromCatalogModal, setShowAddFromCatalogModal }}>
      <>
        <div>
          <div className="mb-3 text-[17px] font-bold text-primaryText">
            Line items {jobLineItemsState?.length > 0 && `(${jobLineItemsState.length})`}
          </div>

          <div className="flex flex-col">
            {/* lint item headers */}
            <If condition={jobLineItemsState?.length > 0}>
              <div
                className="flex flex-row rounded-t-md border bg-tableHeaderBackground px-4 py-2 
                  text-sbase font-medium text-accentText">
                <div className="w-1/3 min-w-[60px] whitespace-nowrap">Item</div>
                <If condition={viewItemCost}>
                  <div className="w-[15%] min-w-[60px] whitespace-nowrap">Unit Cost</div>
                </If>
                <div className="ml-auto w-[15%] min-w-[60px] whitespace-nowrap">Unit price</div>
                <div className="w-[15%] min-w-[60px] whitespace-nowrap text-center">Qty</div>
                <div className="w-[20%] min-w-[60px] whitespace-nowrap pr-5 text-right">Total</div>
              </div>
            </If>
            <div>
              <div className="relative">
                {items?.map((lineItem, index) => {
                  return (
                    <div
                      id={`line-item-div-${index}`}
                      key={lineItem?.itemDescription + index}
                      className={`relative flex min-h-[85px] w-full flex-row
                         border border-t-0 border-borderGray bg-white py-4`}>
                      <div className="flex w-1/3 min-w-[60px] flex-col gap-2 pl-5 pr-2">
                        <div
                          onClick={() => onEditLineItem(lineItem)}
                          className="line-clamp-8 cursor-pointer break-words 
                            text-17px font-bold text-secondary">
                          {lineItem?.itemDescription}
                        </div>
                        <div className="whitespace-pre-line text-sbase font-medium text-accentText line-clamp-3">
                          {lineItem?.additionalDetails}
                        </div>
                      </div>
                      <If condition={viewItemCost}>
                        <div className="flex w-[15%] min-w-[60px] flex-col gap-2">
                          <div className="w-full truncate text-17px font-bold text-primaryText">
                            {lineItem?.originalUnitCost ? getAmountWithCurrency(lineItem?.originalUnitCost, '$') : '$0'}
                          </div>
                          <div className="text-sbase font-medium text-accentText">
                            {lineItem?.markupPercentage}% Margin
                          </div>
                        </div>
                      </If>
                      <div className="ml-auto w-[15%] min-w-[60px] truncate text-17px font-bold text-primaryText">
                        {lineItem?.unitCost ? getAmountWithCurrency(lineItem?.unitCost, '$') : '$0'}
                      </div>
                      <div
                        className="flex w-[15%] min-w-[60px]
                           justify-center truncate text-17px font-semibold text-primaryText">
                        <div className="truncate">{formattedLineItemQty({ qtyString: lineItem?.quantity })}</div>
                      </div>
                      <div className="flex w-[20%] min-w-[60px] flex-col gap-3 pr-5">
                        <div
                          className="truncate text-right
                            text-17px font-semibold text-primaryText">
                          {getAmountWithCurrency(lineItem?.total)}
                        </div>
                        <If condition={lineItem?.taxes?.length > 0}>
                          <div className="text-right text-sbase font-normal text-primaryText">
                            {getLineItemTaxesText(lineItem?.taxes)}
                          </div>
                        </If>
                      </div>
                    </div>
                  );
                })}
              </div>

              {!isPaidInvoice && job.isEditable && canEditJobs() && (
                <div
                  className={twMerge(
                    'border rounded-bl-md border-borderGray',
                    jobLineItemsState?.length === 0 && 'rounded-t'
                  )}>
                  <AddLineItemSectionWrapper
                    onSaveLineItemClick={onSaveLineItemsClick}
                    onModifyLineItemClick={onModifyLineItemClick}
                    context={JobLineItemsSectionContext}
                    from={LINE_ITEM_SOURCE.JOBS}
                    selectIsBackFromCreateTax={selectIsBackFromCreateTaxFromJobsSlice}
                    selectInvoiceDraftData={selectJobLineItemDraftModalData}
                    selectLineItems={selectJobLineItems}
                    onBackFromCreateTax={handleOnBackFromCreateTax}
                    incrementLineItemQty={handleIncrementLineItemQty}
                    selectGlobalLineItemTaxes={selectGlobalLineItemTaxesJobsSlice}
                    updateInvoiceModalData={handleUpdateInvoiceModalData}
                    isFromBillingTab={isFromBillingTab}
                  />
                </div>
              )}

              <div className="-mt-2 mb-2 text-error">{getLineItemErrorMsg()}</div>

              {/* LineItemsTotal */}
              <div className="ml-auto w-1/2 min-w-[405px] rounded-b-md border border-x-borderGray">
                <If condition={discountSettingsState.enabled}>
                  <AddInvoiceDiscountWrapper
                    selectedDiscount={discountFormDataState}
                    isAddDiscountDisabled={isAddDiscountDisabled}
                    isEditInvoiceFlow={false}
                    lineItemsTotalCost={lineItemsTotalState}
                    handleRemoveDiscount={onRemoveDiscount}
                    onSaveDiscount={handleSaveDiscount}
                    isFromBillingTab={isFromBillingTab}
                  />
                </If>

                <div
                  className="flex flex-row justify-between
                      bg-highlightGray/50 p-3 text-17px font-semibold text-heading">
                  <div>Subtotal</div>
                  <div id="create-job-line-items-subtotal-amount" className="w-0 grow break-words text-right">
                    {getFormattedSubtotalCurrency()}
                  </div>
                </div>
                <If condition={job.isServiceFeeEnabled}>
                  {/* <AddServiceFeeWrapper
                    serviceFee={currentSurchargeState}
                    isLoadingTaxesAndFees={isLoadingTaxesAndFees}
                    isEdit={false}
                    selectIsServiceFeeEnabled={selectIsServiceFeeEnabledFromJob}
                    onSaveIsServiceFeeEnabled={handleIsServiceFeeEnabled}
                    isEditDisabled={isPaidInvoice && false}
                    source={LINE_ITEM_SOURCE.JOBS}
                    isFromBillingTab={isFromBillingTab}
                  /> */}
                  <div
                    className="flex flex-row justify-between
                      bg-highlightGray/50 p-3 text-17px font-semibold text-heading">
                    <div>Service fee</div>
                    <div id="create-job-line-items-subtotal-amount" className="w-0 grow break-words text-right">
                      {getAmountWithCurrency(job.surchargeAmount)}
                    </div>
                  </div>
                </If>
                <AddServiceChargeWrapper
                  isAddServiceChargeDisabled={isAddServiceChargeDisabled}
                  selectedServiceCharge={selectedServiceCharge}
                  selectIsBackFromCreateTax={selectIsBackFromCreateTaxFromJobsSlice}
                  selectInvoiceDraftData={selectJobLineItemDraftModalData}
                  onRemoveSelectedServiceCharge={handleRemoveSelectedServiceCharge}
                  onBackFromCreateTax={handleOnBackFromCreateTax}
                  updateSelectedServiceCharge={handleUpdateSelectedServiceCharge}
                  selectGlobalLineItemTaxes={selectGlobalServiceChargeTaxesInJobSlice}
                  updateInvoiceModalData={handleUpdateInvoiceModalData}
                  isFromBillingTab={isFromBillingTab}
                />

                <If condition={selectedAppliedLineItemLevelTaxes?.length > 0}>
                  <div className="flex flex-col border-b border-borderGray p-3">
                    <div className="flex flex-col gap-[13px]">
                      {selectedAppliedLineItemLevelTaxes.map(aggregatedItemTax => (
                        <div className="flex flex-row items-start" key={aggregatedItemTax.id}>
                          <div className="w-1/2 break-words text-17px text-heading ">{aggregatedItemTax.name}</div>
                          {/* <div className="basis-1/4 text-right text-17px text-accentText">
                            {getFormattedAdditionalTaxPercent(aggregatedItemTax.percentage)}%
                          </div> */}
                          <div className="w-1/2 break-words text-right text-17px font-normal text-heading">
                            {getAggregatedTaxInCurrencyWithSymbol(
                              jobLineItemsState,
                              aggregatedItemTax,
                              selectedServiceCharge
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </If>
                <div className="flex flex-col gap-2 p-3 text-[17px] font-bold text-heading">
                  <div className="flex flex-row items-center justify-between">
                    <div>Total amount</div>
                    <div id="create-job-line-items-total-amount" className="grow break-words text-right">
                      {getCurrencyFormattedFinalAmount()}
                    </div>
                  </div>
                  <If condition={viewItemCost}>
                    <div className="flex flex-row items-center justify-between text-sbase text-primaryText">
                      <div>Total unit cost</div>
                      <div id="create-job-line-items-total-cost" className="grow break-words text-right">
                        {getTotalUnitCost()}
                      </div>
                    </div>
                  </If>
                </div>
              </div>
            </div>
          </div>
        </div>

        {addItemModalConfig.open && (
          <AddItemModal
            title="Edit Item"
            isEdit={true}
            onSaveLineItemClick={onSaveLineItemsClick}
            onModifyLineItemClick={onModifyLineItemClick}
            config={addItemModalConfig}
            onClose={onCloseAddItemModal}
            resetItemModalConfig={onCloseAddItemModal}
            isEditInvoiceFlow={false}
            modalType={EItemModalType.EDIT_ITEM_MODAL}
            selectGlobalLineItemTaxes={selectGlobalLineItemTaxesJobsSlice}
            updateInvoiceModalData={handleUpdateInvoiceModalData}
            onRemoveItem={onRemoveLineItem}
            isUpdating={isEditJobLineItemLoading}
          />
        )}
      </>
    </JobLineItemsSectionContext.Provider>
  );
};

export default BillingTabJobLineItemSection;
